import { DataGrid, GridColDef } from '@mui/x-data-grid';
import React from 'react';
import { ChannelMap, Report } from '../../../../redux/reports/api/Report.model';
import { getPolyglot } from '../../../../i18n';
import { useDevicesControlUnits } from '../../../../redux/controlUnit/hooks/useDevicesControlUnits';
import { getTranslationNameByData } from '../../../../redux/monitoring/api/util/getLabelName';
import { useCUsTranslate } from '../../../../redux/CUTranslation/hook/useCUsTranslate';
import useChannelsFromComposableDevice from '../../../../redux/composableDevice/hooks/useChannelsFromComposableDevice';

interface ChannelsMapTableViewProps {
  groupId?: string;
  report: Report;
}
function ChannelsMapTableView(props: ChannelsMapTableViewProps) {
  const { report } = props;
  const polyglot = getPolyglot();
  const [devicesControlUnits, loadingDevicesControlUnits] =
    useDevicesControlUnits();
  const [CUsTranslate, loadingTranslations] = useCUsTranslate();
  const groupId = report.channels.find(
    (channel) => channel.composableDeviceId
  )?.composableDeviceId;
  const { data: composableChannels } = useChannelsFromComposableDevice(
    groupId ?? ''
  );
  const allColumns: GridColDef<ChannelMap>[] = [
    {
      field: 'name',
      headerName: polyglot.t('composable_device.name'),
      minWidth: 150,
      flex: 1,
      valueGetter: (params) => {
        const channelRow = params.row;
        const foundChannel = composableChannels.find(
          (ch) =>
            ch.channelID === channelRow.channelID &&
            ch.cuID === channelRow.cuID &&
            ch.deviceID === channelRow.deviceID
        );
        if (foundChannel?.name) {
          return foundChannel.name;
        }
        // Fallback on the channel translation
        const device = devicesControlUnits.find(
          (item) => item.deviceId === params.row.deviceID
        );
        const controlUnit = device?.controlUnits?.find(
          (cu) => cu.id === params.row.cuID
        );
        const channel = controlUnit?.channels.find(
          (ch) => ch.id === params.row.channelID
        );
        const primaryText = getTranslationNameByData(
          channel?.name,
          channel?.id,
          CUsTranslate,
          params.row.deviceID,
          params.row.cuID
        );
        return primaryText;
      },
    },
    {
      field: 'deviceID',
      headerName: polyglot.t('group.devices.device'),
      minWidth: 150,
      valueGetter: (params) => {
        const device = devicesControlUnits.find(
          (item) => item.deviceId === params.row.deviceID
        );
        return device ? device.deviceName : '';
      },
      flex: 1,
    },
    {
      field: 'cuID',
      headerName: polyglot.t('device.controlUnit.controller'),
      valueGetter: (params) => {
        const device = devicesControlUnits.find(
          (item) => item.deviceId === params.row.deviceID
        );
        const controlUnit = device?.controlUnits?.find(
          (cu) => cu.id === params.row.cuID
        );
        return controlUnit ? controlUnit.name : params.row.cuID;
      },
      minWidth: 150,
      flex: 1,
    },

    {
      field: 'channelID',
      headerName: polyglot.t('reports.channel_map.channel'),
      valueGetter: (params) => {
        const device = devicesControlUnits.find(
          (item) => item.deviceId === params.row.deviceID
        );
        const controlUnit = device?.controlUnits?.find(
          (cu) => cu.id === params.row.cuID
        );
        const channel = controlUnit?.channels.find(
          (ch) => ch.id === params.row.channelID
        );
        const primaryText = getTranslationNameByData(
          channel?.name,
          channel?.id,
          CUsTranslate,
          params.row.deviceID,
          params.row.cuID
        );
        return primaryText;
      },
      minWidth: 120,
      flex: 1,
    },
    {
      field: 'minThreshold',
      headerName: polyglot.t('reports.channel_map.minThreshold'),
      minWidth: 120,
    },
    {
      field: 'maxThreshold',
      headerName: polyglot.t('reports.channel_map.maxThreshold'),
      minWidth: 120,
    },
  ];

  const columns = composableChannels.length
    ? allColumns.filter(
        (c) => !['deviceID', 'cuID', 'channelID'].includes(c.field)
      )
    : allColumns.filter((c) => c.field !== 'name');

  return (
    <>
      <DataGrid
        rows={report.channels}
        columns={columns}
        disableRowSelectionOnClick
        getRowId={(row) =>
          `${row.deviceID}-${row.cuID}-${row.channelID}-${row.maxThreshold}-${row.minThreshold}`
        }
        loading={loadingDevicesControlUnits && loadingTranslations}
        sx={{
          maxWidth: '100%',
        }}
      />
    </>
  );
}

export default ChannelsMapTableView;
